import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box } from '@mui/material'
import React from 'react'
import { FieldArrayWithId, useFieldArray } from 'react-hook-form'
import { BannerLocations, BannerQueue } from 'src/Libs/banners'
import { FormSectionTitle, ScrollRestore } from 'src/Libs/flow/components'
import { InstallationFormModel } from 'src/Libs/flow/installation/forms'
import { useInstallationForm } from 'src/Libs/flow/installation/hooks'
import { ExpandCollapseButtons } from '../../charging-stations'
import AccordionWrapper from '../../components/AccordionWrapper'
import { useSectionFormStatus } from '../../hooks'
import { HideDirtyStateProvider } from '../../providers'
import useExpandCollapseState from '../hooks/use-expand-collapse-state'
import AdvancedSettings from './AdvancedSettings'
import ChargingConfiguration from './ChargingConfiguration'
import { GeneralData } from './GeneralData'
import { SystemRegulation } from './SystemRegulation'
import { WiringTopology } from './WiringTopology'

export const StorageSystems: React.FC = () => {
  return (
    <Box component="section" mb={{ xs: 10, sm: 2 }}>
      <BannerQueue location={BannerLocations.FlowSettingsAdvancedSettingsStorage} sx={{ mt: [2, 0], mb: [0, 2] }} />
      <GeneralStorageSettings />
      <StorageSpecificSettings />
      <ScrollRestore />
    </Box>
  )
}

const GeneralStorageSettings: React.FC = () => {
  const { t } = useCustomTranslation()
  const { isDirty, isError } = useSectionFormStatus(
    dirtyFields => [dirtyFields.storageSystem?.generalSettings],
    errors => [errors.storageSystem?.generalSettings]
  )

  return (
    <AccordionWrapper
      title={<FormSectionTitle name={t('advanced-settings.storage.general-settings.title')} isDirty={isDirty} isError={isError} />}
      expanded
    >
      <WiringTopology />

      <SystemRegulation />

      <ChargingConfiguration>
        <ChargingConfiguration.ChargingPriority />
      </ChargingConfiguration>

      <AdvancedSettings>
        <AdvancedSettings.WeatherForecastOptimization />
        <AdvancedSettings.EmergencyPower>
          <AdvancedSettings.EmergencyPower.EmergencyPowerToggle />
          <AdvancedSettings.EmergencyPower.Delay />
        </AdvancedSettings.EmergencyPower>
        <AdvancedSettings.ChargeBlockingTimes />
        <AdvancedSettings.DischargeBlockingTimes />
      </AdvancedSettings>
    </AccordionWrapper>
  )
}

const StorageSpecificSettings: React.FC = () => {
  const { control } = useInstallationForm()
  const { fields: storageDevices } = useFieldArray<InstallationFormModel, 'storageDevices'>({ control, name: 'storageDevices' })

  const { expandState, areAllCollapsed, areAllExpanded, handleCollapseAll, handleExpandAll, handleToggleExpand } =
    useExpandCollapseState(storageDevices)

  return (
    <>
      {storageDevices.length > 1 && (
        <ExpandCollapseButtons
          align="start"
          disableCollapseAll={areAllCollapsed}
          disableExpandAll={areAllExpanded}
          onCollapseClick={handleCollapseAll}
          onExpandClick={handleExpandAll}
        />
      )}

      {storageDevices.map((storage, index) => (
        <StorageSpecificSettingsItem
          key={storage.id}
          storage={storage}
          storageIndex={index}
          expanded={expandState[storage.deviceId] || false}
          callback={handleToggleExpand(storage.deviceId)}
        />
      ))}
    </>
  )
}

const StorageSpecificSettingsItem: React.FC<{
  storage: FieldArrayWithId<InstallationFormModel, 'storageDevices', 'id'>
  storageIndex: number
  expanded: boolean
  callback: (expanded?: boolean) => void
}> = ({ storage, storageIndex, expanded, callback }) => {
  const { isDirty, isError } = useSectionFormStatus(
    dirtyFields => [dirtyFields.storageSystem?.storageSpecificSettings?.[storageIndex], dirtyFields.storageDevices?.[storageIndex]],
    errors => [errors.storageSystem?.storageSpecificSettings?.[storageIndex], errors.storageDevices?.[storageIndex]]
  )

  return (
    <HideDirtyStateProvider hideDirtyState={storage.isNew}>
      <AccordionWrapper title={<FormSectionTitle name={storage.name} isDirty={isDirty} isError={isError} />} expanded={expanded} callback={callback}>
        <GeneralData storageIndex={storageIndex} />

        <ChargingConfiguration>
          <ChargingConfiguration.TrackersBehaviour storageIndex={storageIndex} />
        </ChargingConfiguration>

        <AdvancedSettings>
          <AdvancedSettings.BatteryNumber storageIndex={storageIndex} />
          <AdvancedSettings.BatteryOperationRange storageIndex={storageIndex} />
          <AdvancedSettings.EmergencyPower>
            <AdvancedSettings.EmergencyPower.Reserve storageIndex={storageIndex} />
          </AdvancedSettings.EmergencyPower>
          <AdvancedSettings.StorageSystemEnergySavingMode storageIndex={storageIndex} />
        </AdvancedSettings>
      </AccordionWrapper>
    </HideDirtyStateProvider>
  )
}
